import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f5f0eb 0%, #faf8f5 100%);
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  align-items: center;
  gap: 6rem;
  position: relative; /* allow decorative images to be absolutely positioned inside */
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 3rem;
    text-align: center;
  }
`;

const DecorativeImg = styled.img`
  position: absolute;
  width: 15.5%; 
  height: auto;
  pointer-events: none;
  display: block;

  @media (max-width: 768px) {
    width: 15%;
  }
`;

const ImageContainer = styled(motion.div)`
  /* make container responsive so internal percentages scale */
  flex: 0 0 auto;
  width: 100%;
  max-width: 550px;

  @media (max-width: 768px) {
    max-width: 350px;
  }
`;

const PortfolioImage = styled.div`
  width: 100%;
  max-width: 550px;
  /* preserve aspect ratio so overlay bottom % behaves predictably */
  aspect-ratio: 550 / 650;
  overflow: visible; /* allow mic wrapper and image to extend slightly without horizontal clipping */
  position: relative; /* allow overlay positioning */

  img {
    width: 100%;
    height: 100%;
    object-fit: contain; /* prevent cropping of image edges */
    display: block;
    position: relative;
    z-index: 2; /* ensure portrait sits above overlay */
  }

  @media (max-width: 768px) {
    max-width: 350px;
    aspect-ratio: 350 / 420;
  }
`;

const MicWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  /* width is percentage of the portrait container so it scales */
  width: 402%;
  /* clip the top of the mic so only the lower part (stand) shows */
  height: 36%;
  bottom: 0%;
  overflow: hidden;
  pointer-events: none;
  z-index: 3; /* sit above portrait but only bottom part visible */

  @media (max-width: 768px) {
    /* make mic more prominent on phones */
    /* make mic more prominent on phones and drop it lower */
    width: 70%;
    height: 90%;
    bottom: -8%;
  }
`;

const MicOverlay = styled.img`
  position: absolute;
  bottom: 0; /* align the mic image so its bottom aligns with wrapper bottom */
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: auto;
  filter: drop-shadow(0 6px 12px rgba(0,0,0,0.12));
  
  @media (max-width: 768px) {
    width: 220%;
  }
`;

const TextContainer = styled(motion.div)`
  flex: 1;
  max-width: 600px;
`;

const Title = styled.h2`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 300;
  color: #d4737d;
  margin: 0 0 2rem 0;
  font-family: 'Playfair Display', serif;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const Description = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  line-height: 1.8;
  color: #5a5a5a;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 400;
  margin: 0 0 2rem 0;
  
  &::first-letter {
    font-size: 3em;
    float: left;
    line-height: 0.8;
    margin: 0.1em 0.1em 0 0;
    color: #d4737d;
    font-weight: 600;
  }
`;

const Highlight = styled.span`
  color: #d4737d;
  font-weight: 500;
  font-style: italic;
`;

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    // set initial value in case SSR or late mount
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      
      // Show about section when scrolled past hero
      if (scrollPosition > windowHeight * 0.7) {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const imageVariants = {
    hidden: { x: -100, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const textVariants = {
    hidden: { x: 100, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut", delay: 0.2 }
    }
  };

  return (
    <AboutContainer>
      <ContentWrapper>
        <ImageContainer
          variants={imageVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {/* decorative number images (grouped with portrait so they move together) */}
          <DecorativeImg src="/4.png" alt="decorative 4" style={{ left: '33%', top: '10%' }} />
          <DecorativeImg src="/9.png" alt="decorative 9" style={{ left: '0%', top: '28%' }} />
          <PortfolioImage>
            {!imageError ? (
              <img
                src="/pp.png"
                alt="Saule Skodminaite - Portfolio Owner"
                onError={() => setImageError(true)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#a0a0a0',
                fontFamily: "'Cormorant Garamond', serif",
                textAlign: 'center'
              }}>Portfolio Owner<br />Photo Here</div>
            )}

            {/* Microphonee overlay placed on top of the portrait (clipped to show only lower part) */}
            <MicWrapper
              aria-hidden="true"
              style={isMobile ? { width: '70%', height: '90%', bottom: '-30%' } : undefined}
            >
              <MicOverlay
                src="/mics.png"
                alt="microphone overlay"
                style={isMobile ? { width: '120%' } : undefined}
              />
            </MicWrapper>
          </PortfolioImage>
        </ImageContainer>

        <TextContainer
          variants={textVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          <Title>About Saulė</Title>
          <Description>
            I'm a 21-year-old Creative Business student from <Highlight>Lithuania</Highlight>,
            learning to blend creative vision with strategic thinking. I love telling
            stories through visuals and campaigns, and I'm curious about how small
            ideas grow into memorable brand moments.
          </Description>
          <Description>
            My work spans <Highlight>social content</Highlight>, <Highlight>brand identity</Highlight>,
            and hands-on photography projects. I focus on authentic connection—making
            thoughtful creative that feels personal, purposeful, and ready for the real world.
          </Description>
        </TextContainer>
      </ContentWrapper>
    </AboutContainer>
  );
};

export default About;